<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-4 page-header">
      <v-card-title class="text-h5 font-weight-bold pa-0">
        <v-icon left color="#4a3aff">mdi-chat-question</v-icon>
        {{ $t('suggestions.title') }}
      </v-card-title>

      <v-btn color="primary" :loading="loading" data-tour="suggestions-refresh" @click="loadSuggestions">
        <v-icon left>mdi-refresh</v-icon>
        {{ $t('common.refresh') }}
      </v-btn>
    </div>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <div v-if="isSuperAdmin">
      <v-tabs v-model="activeTab" background-color="transparent" color="#4a3aff" class="mb-4 suggestions-tabs d-none d-md-block">
        <v-tab v-for="tab in superAdminTabs" :key="tab.value">
          <v-icon left>{{ tab.icon }}</v-icon>
          {{ $t(tab.labelKey) }}
          <v-chip small class="ml-2" color="#e5e0ff">{{ countByType(tab.value) }}</v-chip>
        </v-tab>
      </v-tabs>

      <div class="suggestion-mobile-filters d-md-none">
        <v-btn
          v-for="(tab, index) in superAdminTabs"
          :key="tab.value"
          small
          depressed
          class="suggestion-filter-btn"
          :class="{ 'suggestion-filter-btn--active': activeTab === index }"
          @click="activeTab = index"
        >
          <v-icon left small>{{ tab.icon }}</v-icon>
          <span>{{ $t(tab.labelKey) }}</span>
          <v-chip x-small class="ml-2 filter-count">{{ countByType(tab.value) }}</v-chip>
        </v-btn>
      </div>

      <v-card class="pa-5 rounded-lg suggestions-panel">
        <v-data-table
          :headers="superAdminHeaders"
          :items="filteredSuggestions"
          :loading="loading"
          sort-by="createdAtSort"
          :sort-desc="true"
          class="elevation-0 d-none d-md-block"
        >
          <template #[`item.typeLabel`]="{ item }">
            <v-chip small :color="typeMeta(item.type).color" dark>
              <v-icon left small>{{ typeMeta(item.type).icon }}</v-icon>
              {{ item.typeLabel }}
            </v-chip>
          </template>

          <template #[`item.author`]="{ item }">
            <div class="font-weight-medium">{{ fullName(item) }}</div>
            <div class="text-caption grey--text text--darken-1">{{ item.userEmail || item.email }}</div>
          </template>

          <template #[`item.suggestionText`]="{ item }">
            <span class="suggestion-cell">{{ item.suggestionText }}</span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>

          <template #[`item.viewedStatus`]="{ item }">
            <v-chip v-if="item.type !== 'agreement'" small :color="item.viewedAt ? 'green lighten-4' : 'amber lighten-4'" class="suggestion-status-chip">
              <v-icon left small>{{ item.viewedAt ? 'mdi-eye-check' : 'mdi-eye-off-outline' }}</v-icon>
              {{ item.viewedAt ? $t('suggestions.viewed') : $t('suggestions.notViewed') }}
            </v-chip>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="canMarkSuggestionViewed(item)"
              small
              text
              color="primary"
              :loading="markingId === item.id"
              @click="markSuggestionViewed(item)"
            >
              <v-icon left small>mdi-eye-check-outline</v-icon>
              {{ $t('suggestions.markViewed') }}
            </v-btn>
          </template>

          <template #no-data>
            {{ $t('suggestions.emptyCategory') }}
          </template>
        </v-data-table>

        <div class="suggestion-mobile-list d-md-none">
          <v-progress-linear v-if="loading" indeterminate color="#4a3aff" class="mb-3" />

          <div v-if="!loading && !filteredSuggestions.length" class="empty-mobile">
            {{ $t('suggestions.emptyCategory') }}
          </div>

          <v-card
            v-for="item in filteredSuggestions"
            :key="item.id"
            outlined
            class="suggestion-mobile-card"
          >
            <div class="suggestion-mobile-top">
              <v-chip small :color="typeMeta(item.type).color" dark class="suggestion-type-chip">
                <v-icon left small>{{ typeMeta(item.type).icon }}</v-icon>
                {{ item.typeLabel }}
              </v-chip>
            </div>

            <div class="suggestion-meta-block">
              <div class="suggestion-company">
                {{ item.company || $t('common.noCompany') }}
              </div>
              <div class="suggestion-date">{{ formatDateTime(item.createdAt) }}</div>
            </div>

            <p class="suggestion-text">
              {{ item.suggestionText }}
            </p>

            <div class="suggestion-author">
              <strong>{{ fullName(item) }}</strong>
              <span>{{ item.userEmail || item.email }}</span>
            </div>

            <div v-if="isSuperAdmin && item.type !== 'agreement'" class="suggestion-mobile-actions">
              <v-chip small :color="item.viewedAt ? 'green lighten-4' : 'amber lighten-4'" class="suggestion-status-chip">
                <v-icon left small>{{ item.viewedAt ? 'mdi-eye-check' : 'mdi-eye-off-outline' }}</v-icon>
                {{ item.viewedAt ? $t('suggestions.viewed') : $t('suggestions.notViewed') }}
              </v-chip>

              <v-btn
                v-if="canMarkSuggestionViewed(item)"
                small
                text
                color="primary"
                :loading="markingId === item.id"
                @click="markSuggestionViewed(item)"
              >
                <v-icon left small>mdi-eye-check-outline</v-icon>
                {{ $t('suggestions.markViewed') }}
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-card>
    </div>

    <div v-else>
      <v-card class="pa-5 rounded-lg mb-5 suggestions-panel">
        <div class="d-flex align-center justify-space-between mb-3 suggestions-section-header">
          <v-card-title class="text-h6 font-weight-bold pa-0">
            <v-icon left color="#4a3aff">mdi-book-open-page-variant</v-icon>
            {{ $t('suggestions.agreementTitle') }}
          </v-card-title>
        </div>

        <v-data-table
          :headers="managerHeaders"
          :items="filteredSuggestions"
          :loading="loading"
          sort-by="createdAtSort"
          :sort-desc="true"
          class="elevation-0 d-none d-md-block"
        >
          <template #[`item.author`]="{ item }">
            <div class="font-weight-medium">{{ fullName(item) }}</div>
            <div class="text-caption grey--text text--darken-1">{{ item.userEmail || item.email }}</div>
          </template>

          <template #[`item.suggestionText`]="{ item }">
            <span class="suggestion-cell">{{ item.suggestionText }}</span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>

          <template #no-data>
            {{ $t('suggestions.emptyAgreement') }}
          </template>
        </v-data-table>

        <div class="suggestion-mobile-list d-md-none">
          <v-progress-linear v-if="loading" indeterminate color="#4a3aff" class="mb-3" />

          <div v-if="!loading && !filteredSuggestions.length" class="empty-mobile">
            {{ $t('suggestions.emptyAgreement') }}
          </div>

          <v-card
            v-for="item in filteredSuggestions"
            :key="item.id"
            outlined
            class="suggestion-mobile-card"
          >
            <div class="suggestion-mobile-top">
              <v-chip small color="#4a3aff" dark class="suggestion-type-chip">
                <v-icon left small>mdi-book-open-page-variant</v-icon>
                {{ $t('suggestions.types.agreement') }}
              </v-chip>
            </div>

            <p class="suggestion-text">
              {{ item.suggestionText }}
            </p>

            <div class="suggestion-author">
              <strong>{{ fullName(item) }}</strong>
              <span>{{ item.userEmail || item.email }}</span>
              <span>{{ formatDateTime(item.createdAt) }}</span>
            </div>
          </v-card>
        </div>
      </v-card>

      <v-card class="pa-6 rounded-lg suggestion-form-card" data-tour="suggestions-form">
        <v-card-title class="text-h5 font-weight-bold">
          <v-icon left color="#4a3aff">mdi-lightbulb-on-outline</v-icon>
          {{ $t('suggestions.platformTitle') }}
        </v-card-title>

        <v-card-text>
          <p class="text-body-2 mb-4">
            {{ $t('suggestions.platformIntro') }}
          </p>

          <v-form ref="suggestionForm" v-model="suggestionValid">
            <v-textarea
              v-model="form.suggestion"
              :label="$t('suggestions.placeholder')"
              outlined
              auto-grow
              rows="5"
              :rules="[rules.required]"
              required
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn color="primary" dark rounded :loading="saving" @click="saveSuggestion">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('suggestions.send') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { db } from "@/firebase";
import { collection, addDoc, doc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";

const SUGGESTION_TYPES = {
  agreement: {
    labelKey: "suggestions.types.agreement",
    icon: "mdi-book-open-page-variant",
    color: "#4a3aff",
  },
  app: {
    labelKey: "suggestions.types.app",
    icon: "mdi-cellphone",
    color: "#00897b",
  },
  platform: {
    labelKey: "suggestions.types.platform",
    icon: "mdi-monitor-dashboard",
    color: "#6d4c41",
  },
};

export default {
  name: "Suggestions",

  data() {
    return {
      activeTab: 0,
      loading: false,
      saving: false,
      markingId: "",
      suggestionValid: false,
      message: "",
      messageType: "success",
      suggestions: [],
      form: {
        suggestion: "",
      },
      superAdminTabs: [
        { value: "app", labelKey: "suggestions.appTab", icon: "mdi-cellphone" },
        { value: "platform", labelKey: "suggestions.platformTab", icon: "mdi-monitor-dashboard" },
        { value: "agreement", labelKey: "suggestions.companiesTab", icon: "mdi-domain" },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    isSuperAdmin() {
      return this.$store.getters["auth/isSuperAdmin"];
    },
    companyId() {
      return this.$store.getters["auth/companyId"];
    },
    companyName() {
      return this.$store.getters["auth/companyName"];
    },
    selectedType() {
      return this.superAdminTabs[this.activeTab]?.value || "app";
    },
    filteredSuggestions() {
      if (this.isSuperAdmin) {
        return this.suggestions.filter((item) => item.type === this.selectedType);
      }

      return this.suggestions.filter((item) => item.type === "agreement");
    },
    superAdminHeaders() {
      return [
        { text: this.$t("common.type"), value: "typeLabel" },
        { text: this.$t("common.company"), value: "company" },
        { text: this.$t("common.author"), value: "author" },
        { text: this.$t("suggestions.title"), value: "suggestionText" },
        { text: this.$t("common.date"), value: "createdAt" },
        { text: this.$t("common.status"), value: "viewedStatus", sortable: false },
        { text: this.$t("common.actions"), value: "actions", sortable: false, align: "end" },
      ];
    },
    managerHeaders() {
      return [
        { text: this.$t("common.author"), value: "author" },
        { text: this.$t("suggestions.title"), value: "suggestionText" },
        { text: this.$t("common.date"), value: "createdAt" },
      ];
    },
    rules() {
      return {
        required: (value) => !!(value && value.trim()) || this.$t("suggestions.missingText"),
      };
    },
  },

  watch: {
    companyId() {
      this.loadSuggestions();
    },
  },

  async created() {
    await this.loadSuggestions();
  },

  methods: {
    async loadSuggestions() {
      this.loading = true;
      this.message = "";

      try {
        const suggestionsQuery = this.isSuperAdmin
          ? collection(db, "suggestions")
          : query(
            collection(db, "suggestions"),
            where("companyId", "==", this.companyId || ""),
            where("type", "==", "agreement")
          );

        const snap = await getDocs(suggestionsQuery);
        this.suggestions = snap.docs
          .map((suggestionDoc) => this.mapSuggestion(suggestionDoc.id, suggestionDoc.data()))
          .filter((item) => this.isSuperAdmin || item.type === "agreement")
          .sort((a, b) => b.createdAtSort - a.createdAtSort);
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "suggestions.loadError", this.$i18n), "error");
      } finally {
        this.loading = false;
      }
    },

    async saveSuggestion() {
      if (!this.$refs.suggestionForm.validate()) {
        this.showMessage(this.$t("suggestions.missingText"), "error");
        return;
      }

      if (!this.companyName) {
        this.showMessage(this.$t("suggestions.missingCompany"), "error");
        return;
      }

      this.saving = true;

      try {
        await addDoc(
          collection(db, "suggestions"),
          {
            type: "platform",
            source: "web",
            company: this.companyName,
            companyId: this.companyId || "",
            userId: this.user?.uid || "",
            createdByUid: this.user?.uid || "",
            userName: this.user?.nombre || "",
            userLastName: this.user?.apellidos || "",
            userEmail: this.user?.email || "",
            email: this.user?.email || "",
            suggestion: this.form.suggestion.trim(),
            createdAt: serverTimestamp(),
            viewedAt: null,
            viewedByUid: "",
          }
        );

        this.showMessage(this.$t("suggestions.thanks"), "success");
        this.form.suggestion = "";
        this.$nextTick(() => this.$refs.suggestionForm?.resetValidation());
      } catch (err) {
        this.showMessage(getUserErrorMessage(err, "suggestions.saveError", this.$i18n), "error");
      } finally {
        this.saving = false;
      }
    },

    mapSuggestion(id, data) {
      const type = this.getSuggestionType(data);

      return {
        id,
        ...data,
        type,
        typeLabel: this.typeMeta(type).label,
        userName: data.userName || data.nombre || "",
        userLastName: data.userLastName || data.apellidos || "",
        userEmail: data.userEmail || data.email || "",
        email: data.email || data.userEmail || "",
        suggestionText: this.getSuggestionText(data),
        viewedAt: data.viewedAt || null,
        viewedByUid: data.viewedByUid || "",
        createdAtSort: this.toMillis(data.createdAt),
      };
    },

    canMarkSuggestionViewed(item) {
      return this.isSuperAdmin && ["app", "platform"].includes(item.type) && !item.viewedAt;
    },

    async markSuggestionViewed(item) {
      if (!this.canMarkSuggestionViewed(item)) return;

      this.markingId = item.id;

      try {
        await updateDoc(doc(db, "suggestions", item.id), {
          viewedAt: serverTimestamp(),
          viewedByUid: this.user?.uid || "",
          viewedByEmail: this.user?.email || "",
        });

        item.viewedAt = new Date();
        item.viewedByUid = this.user?.uid || "";
        item.viewedByEmail = this.user?.email || "";
        this.showMessage(this.$t("suggestions.markViewedSuccess"), "success");
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "suggestions.markViewedError", this.$i18n), "error");
      } finally {
        this.markingId = "";
      }
    },

    countByType(type) {
      return this.suggestions.filter((item) => item.type === type).length;
    },

    typeMeta(type) {
      const meta = SUGGESTION_TYPES[type] || SUGGESTION_TYPES.agreement;
      return {
        ...meta,
        label: this.$t(meta.labelKey),
      };
    },

    fullName(item) {
      return `${item.userName || ""} ${item.userLastName || ""}`.trim() || this.$t("common.noName");
    },

    getSuggestionText(item) {
      return item.suggestion || item.mensaje || "";
    },

    getSuggestionType(item) {
      if (SUGGESTION_TYPES[item.type]) return item.type;
      if (SUGGESTION_TYPES[item.category]) return item.category;
      return "agreement";
    },

    formatDateTime(value) {
      if (!value) return "";
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString(this.$i18n.locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    toMillis(value) {
      if (!value) return 0;
      const date = value?.toDate ? value.toDate() : new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    },
  },
};
</script>

<style scoped>
.v-card {
  background-color: #f8f7fd;
}

.suggestion-cell {
  display: inline-block;
  max-width: 520px;
  white-space: normal;
}

.suggestion-mobile-list {
  display: grid;
  gap: 12px;
}

.suggestion-mobile-filters {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
  margin-bottom: 12px;
}

.suggestion-filter-btn {
  background: #f8f7fd !important;
  border: 1px solid rgba(74, 58, 255, 0.12);
  border-radius: 10px;
  color: #3a3551 !important;
  justify-content: flex-start;
  letter-spacing: 0;
  min-height: 42px;
  text-transform: none;
}

.suggestion-filter-btn--active {
  background: #4a3aff !important;
  color: #fff !important;
}

.suggestion-filter-btn--active .filter-count {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff;
}

.suggestion-mobile-card {
  background: #fff !important;
  border-color: rgba(74, 58, 255, 0.12) !important;
  border-radius: 12px;
  padding: 14px;
}

.suggestion-mobile-top {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  margin-bottom: 10px;
  min-width: 0;
}

.suggestion-type-chip {
  max-width: 100%;
}

.suggestion-type-chip ::v-deep .v-chip__content {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-meta-block {
  background: #f8f7fd;
  border-radius: 10px;
  margin-bottom: 10px;
  padding: 8px 10px;
}

.suggestion-date {
  color: #777;
  font-size: 0.74rem;
  line-height: 1.35;
}

.suggestion-company {
  color: #3a3551;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 8px;
  overflow-wrap: anywhere;
}

.suggestion-text {
  color: #424242;
  font-size: 0.92rem;
  line-height: 1.45;
  margin: 0 0 12px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.suggestion-author {
  border-top: 1px solid #eee;
  color: #6b6478;
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  gap: 2px;
  padding-top: 10px;
}

.suggestion-author strong,
.suggestion-author span {
  overflow-wrap: anywhere;
}

.suggestion-status-chip {
  color: #3a3551;
}

.suggestion-mobile-actions {
  align-items: center;
  border-top: 1px solid #eee;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
}

.empty-mobile {
  background: #fff;
  border: 1px dashed rgba(74, 58, 255, 0.18);
  border-radius: 12px;
  color: #777;
  font-size: 0.9rem;
  padding: 18px 12px;
  text-align: center;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

@media (max-width: 960px) {
  .v-container {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .page-header {
    align-items: stretch !important;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 14px !important;
  }

  .page-header .v-btn {
    width: 100%;
  }

  .page-header .v-card__title {
    font-size: 1.2rem !important;
    line-height: 1.3;
  }

  .v-card {
    padding: 14px !important;
  }

  .suggestions-panel {
    background: #f8f7fd !important;
    border-radius: 12px !important;
  }

  .suggestion-mobile-list {
    grid-template-columns: 1fr;
  }

  .suggestions-section-header {
    align-items: flex-start !important;
    flex-direction: column;
    gap: 8px;
  }

  .suggestions-section-header .v-card__title,
  .suggestion-form-card .v-card__title {
    font-size: 1.05rem !important;
    line-height: 1.25;
    padding: 0 !important;
  }

  .suggestion-cell {
    max-width: 240px;
  }

  .suggestions-tabs ::v-deep .v-tabs-bar {
    overflow-x: auto;
  }

  .suggestions-tabs ::v-deep .v-tabs-bar__content {
    min-width: max-content;
  }

  .suggestions-tabs ::v-deep .v-tab {
    min-width: 0;
    padding: 0 12px;
  }

  .suggestions-tabs ::v-deep .v-tab .v-chip {
    margin-left: 6px !important;
  }

  .suggestion-form-card .v-card__text {
    padding: 12px 0 0 !important;
  }

  .suggestion-form-card .v-card__actions {
    padding: 0 !important;
  }

  .suggestion-form-card .v-btn {
    width: 100%;
  }

  .suggestion-mobile-top {
    align-items: flex-start;
  }

  .suggestion-date {
    max-width: none;
    text-align: left;
  }
}

@media (max-width: 600px) {
  .suggestion-mobile-list {
    grid-template-columns: 1fr;
  }

  .suggestion-mobile-card {
    padding: 12px !important;
  }
}

</style>
